import Service, { inject as service } from '@ember/service';
import { union, notEmpty, reads, bool, filterBy } from '@ember/object/computed';
import { computed } from '@ember/object';
import ApiErrorsService from 'cio/services/api-errors';
import DS from 'ember-data';
import FacebookLeadAdsCredential from 'cio/models/facebook-lead-ads-credential';
import ApiService from 'cio/services/api';
import FacebookLeadAdsForm, { FacebookLeadAdsFormStatus } from 'cio/models/facebook-lead-ads-form';
import FeaturesService from 'ember-feature-flags/services/features';
import EnvironmentService from './environment';
import AlertsService from './alerts';
import AnalyticsService from './analytics';
import FacebookConnectService from 'cio/services/facebook-connect';
import { NO_OAUTH_RESPONSE } from 'cio/utils/oauth';

export const requiredFacebookAccessTokenScopes = [
  'ads_management',
  'pages_show_list',
  'pages_manage_ads',
  'pages_manage_metadata',
  'leads_retrieval',
  'pages_read_engagement',
  // Required to list pages that a user is connected to via a business.
  'business_management',
];

export type FacebookBusinessPageData = {
  name: string;
  id: string;
  accessToken: string;
};

// matches specific back-end error response:
export const FBAccessTokenExpiredErrorMessage = 'expired facebook credentials';

export class FBAccessTokenMissingScopesError extends Error {}

export default class FacebookLeadAdsService extends Service {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service facebookConnect!: FacebookConnectService;
  @service store!: DS.Store;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;

  initialized = false;
  credentialsExpired = false;
  credential: FacebookLeadAdsCredential | null = null;
  forms: FacebookLeadAdsForm[] = [];

  @bool('credential') enabled!: boolean;

  @filterBy('forms', 'isConnected') connectedForms!: FacebookLeadAdsForm[];
  @filterBy('forms', 'isDisconnected') disconnectedForms!: FacebookLeadAdsForm[];
  @filterBy('forms', 'canBeConnected') connectableDisconnectedForms!: FacebookLeadAdsForm[];
  @filterBy('forms', 'isOrWasConnected') connectedNowOrBeforeForms!: FacebookLeadAdsForm[];
  @union('connectedForms', 'connectableDisconnectedForms') usableForms!: FacebookLeadAdsForm[];

  @notEmpty('connectableDisconnectedForms') hasFormsToConnect!: boolean;

  @reads('environment.current.canIdentifyByEmail') isAvailable!: boolean;

  @computed('credential.pageId')
  get manageInFacebookURL() {
    if (!this.credential) {
      return '';
    }

    return `https://business.facebook.com/latest/instant_forms/forms?asset_id=${this.credential.pageId}`;
  }

  @computed('forms.[]')
  get formsByFormID() {
    return this.forms.reduce(
      (aggr: Record<string, FacebookLeadAdsForm>, f: FacebookLeadAdsForm) => ({
        ...aggr,
        [f.facebookFormId]: f,
      }),
      {},
    );
  }

  async initialize(forceRefresh = false) {
    if (this.initialized && !forceRefresh) {
      return;
    }

    let credential = await this.fetchCredential();

    if (!credential) {
      this.set('initialized', true);
      return;
    }

    await this.fetchForms();

    this.set('initialized', true);
  }

  async fetchCredential() {
    let credential!: FacebookLeadAdsCredential | null;

    try {
      credential = await this.store.findRecord('facebook-lead-ads-credential', 'current');
    } catch (err) {
      // ignore 404 errors: it simply means this environment doesn't have FB Lead Ads integration enabled, which is fine
      if (err.errors?.[0]?.status !== '404') {
        throw err;
      }
    }

    if (credential) {
      this.set('credential', credential);
    }

    return credential;
  }

  async fetchForms() {
    let forms = await this.store.query('facebook-lead-ads-form', {});

    this.set('forms', forms.toArray());

    return forms;
  }

  // called when switching workspaces:
  unload() {
    this.setProperties({
      initialized: false,
      credential: null,
      forms: [],
    });
  }

  // Auth with Facebook, fetch and return:
  // - short-lived Facebook user access token
  // - Facebook business pages data -> for selection in UI
  // - Facebook user account name -> for display in UI
  async setupStart(from: string) {
    this.analytics.track('facebook_lead_ads', 'connect_account_started', {
      from,
    });

    let facebookAuthResponse: fb.StatusResponse['authResponse'];
    try {
      facebookAuthResponse = await this.facebookConnect.login({ feature: 'lead_ads' });
    } catch (err) {
      // If the user cancelled the signup process, we don't want to report an error
      // Facebook/oauth for some reason returns an error "unknown"
      if (err.message !== NO_OAUTH_RESPONSE && err !== 'unknown') {
        this.apiErrors.report(err);
      }

      let errorMessage = 'Error authenticating with Facebook.';
      this.analytics.track('integration', 'error_encountered', {
        name: 'Facebook Lead Ads',
        step: 'Facebook OAuth',
        message: errorMessage,
        source: from,
      });
      this.alerts.error('facebookLeadAdsSetup.error', errorMessage);

      throw err;
    }

    // Check received Facebook access token for permissions needed for Lead Ads integration:
    // (Requires use of an App Secret, and so has to be done through Customer.io API)
    let accessTokenScopes = (
      (await this.api.request(`facebook_access_token_scopes?access_token=${facebookAuthResponse.accessToken}`, {
        pathScope: 'global',
      })) as { scopes: string[] }
    ).scopes;
    if (requiredFacebookAccessTokenScopes.some((scope) => !accessTokenScopes.includes(scope))) {
      let errorMessage = 'You must grant the requested permissions for at least one of your Facebook Business pages.';
      this.analytics.track('integration', 'error_encountered', {
        name: 'Facebook Lead Ads',
        step: 'Connect Facebook Business Page',
        message: errorMessage,
        source: from,
      });

      this.alerts.error('facebookLeadAds.enable.error', errorMessage);

      throw new FBAccessTokenMissingScopesError();
    }

    this.analytics.track('facebook_lead_ads', 'connect_account_completed');

    let currentUserResponsePromise: Promise<{ id: string; name: string; error?: string }> = new Promise(
      // Call Facebook API to fetch used FB user account name: needed to display in UI
      (resolve, reject) => {
        FB.api('/me', 'get', {}, (response: { name: string; id: string; error?: string }) => {
          if (!response || response.error) {
            return reject(response?.error);
          }

          return resolve(response);
        });
      },
    );

    // Call Facebook API to fetch list of accessible Business Pages, which user can select to link with this CIO environment
    // (no need to verify the response: at least one is guaranteed to be available after token scopes check above)
    let pagesResponsePromise: { data: Record<string, any>[]; error?: string; paging: { next?: string } } =
      await new Promise((resolve, reject) => {
        // No pagination: we don't assume users managing or granting us permission to a lot of them
        FB.api(
          '/me/accounts',
          'get',
          {},
          (response: {
            data: Record<string, any>[];
            error?: string;
            accessToken: string;
            paging: { next?: string };
          }) => {
            if (!response || response.error) {
              return reject(response?.error);
            }

            return resolve(response);
          },
        );
      });

    return {
      userAccessToken: facebookAuthResponse.accessToken,
      pages: pagesResponsePromise.data.map((p: { id: string; name: string; access_token: string }) => ({
        id: p.id,
        name: p.name,
        accessToken: p.access_token,
      })),
      morePages: pagesResponsePromise?.paging?.next,
      facebookAccountName: (await currentUserResponsePromise).name,
    };
  }

  async fetchMorePages(next: string) {
    let result = await new Promise<{ data: Record<string, any>[]; paging: { next?: string }; error?: string }>(
      (resolve, reject) => {
        FB.api(
          next,
          'get',
          {},
          (response: {
            data: Record<string, any>[];
            paging: { next?: string };
            error?: string;
            accessToken: string;
          }) => {
            if (!response || response.error) {
              return reject(response?.error);
            }

            return resolve(response);
          },
        );
      },
    );

    return {
      pages: result.data.map((p: { id: string; name: string; access_token: string }) => ({
        id: p.id,
        name: p.name,
        accessToken: p.access_token,
      })),
      morePages: result?.paging?.next,
    };
  }

  async setupFinish({ userAccessToken, pageId, from }: { userAccessToken: string; pageId: string; from: string }) {
    try {
      let reconnecting = Boolean(this.credential);
      let credential = this.credential ?? this.store.createRecord('facebook-lead-ads-credential');
      credential.setProperties({
        userAccessToken,
        pageId,
      });

      await credential.save();
      let forms = await this.store.query('facebook-lead-ads-form', {});

      this.setProperties({
        credentialsExpired: false,
        credential,
        forms,
      });

      if (reconnecting) {
        this.alerts.success('facebookLeadAds.reconnect.success', 'Facebook Business Page reconnected');
      } else if (this.connectableDisconnectedForms.length > 0) {
        this.alerts.success(
          'facebookLeadAds.enable.success',
          'Facebook Lead Ads enabled. Now you can setup your Lead Ads forms with Customer.io!',
        );
      } else {
        this.alerts.warning(
          'facebookLeadAds.enable.successNoForms',
          'Facebook Lead Ads enabled, but there are no available forms to connect.',
        );
      }
    } catch (err) {
      let errorMessage = 'An error occurred. Please contact win@customer.io for help enabling Facebook Lead Ads.';

      this.analytics.track('integration', 'error_encountered', {
        name: 'Facebook Lead Ads',
        step: 'Connect Facebook Business Page',
        error: errorMessage,
        source: from,
      });

      this.alerts.error('facebookLeadAds.enable.error', errorMessage);
    }
  }

  async disable() {
    if (!this.credential) {
      return;
    }

    await this.credential.destroyRecord();
    this.setProperties({
      credential: null,
      forms: [],
    });
  }

  async updateAllForms() {
    try {
      let blankForm = this.store.createRecord('facebook-lead-ads-form');
      await blankForm.updateAll({});
      blankForm.unloadRecord();

      this.set('forms', await this.store.query('facebook-lead-ads-form', {}));
    } catch (e) {
      if (e.errors?.[0]?.detail === FBAccessTokenExpiredErrorMessage) {
        this.set('credentialsExpired', true);
        return;
      }

      this.apiErrors.report(e);
    }
  }

  async connectForm(form: FacebookLeadAdsForm) {
    if (form.status !== FacebookLeadAdsFormStatus.Active) {
      throw new Error('New form status must be active');
    }

    return this._updateForm(form, FacebookLeadAdsFormStatus.Inactive);
  }

  async disconnectForm(form: FacebookLeadAdsForm) {
    if (form.status !== FacebookLeadAdsFormStatus.Inactive) {
      throw new Error('New form status must be inactive');
    }

    return this._updateForm(form, FacebookLeadAdsFormStatus.Active);
  }

  async updateFormSettings(form: FacebookLeadAdsForm) {
    if (form.status !== FacebookLeadAdsFormStatus.Active) {
      throw new Error('Form must be active to manage its settings');
    }

    return this._updateForm(form);
  }

  private async _updateForm(form: FacebookLeadAdsForm, originalStatus?: FacebookLeadAdsFormStatus) {
    try {
      await form.save();

      let isConnecting = originalStatus === FacebookLeadAdsFormStatus.Inactive;
      let isDisconnecting =
        originalStatus === FacebookLeadAdsFormStatus.Active && form.status === FacebookLeadAdsFormStatus.Inactive;

      if (isConnecting) {
        this.alerts.success('facebookLeadAds.form.connect.success', 'Facebook Lead Ads form connected');
      } else if (isDisconnecting) {
        this.alerts.success('facebookLeadAds.form.disconnect.success', 'Facebook Lead Ads form disconnected');
      } else {
        this.alerts.success('facebookLeadAds.form.updated.success', 'Facebook Lead Ads form settings updated');
      }
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'facebook-lead-ads': FacebookLeadAdsService;
  }
}
